/**
 * Tipos da tela Configuração de Bonds (Brief 4 V2 · Tela 9 · ADR-037/131).
 *
 * Hand-written, espelhando o serializer real do backend
 * (`apps/engine/src/modules/bond-type-configs/bond-type-configs.service.ts` →
 * `BondTypeConfigView`), confirmado no R15 ao vivo. Os enums (`BondType`,
 * `BondAdversarialMode`, `BondSide`, `BondLifecycle`) reusam o catálogo canônico
 * de `@lume/shared` — fonte única dos 21 tipos + defaults.
 */

import type { BondAdversarialMode, BondLifecycle, BondSide, BondType } from '@lume/shared';

export type { BondType, BondAdversarialMode, BondSide, BondLifecycle };

/**
 * Visão mesclada catálogo canônico × estado do tenant para UM tipo de Bond
 * (GET /v1/bond-type-configs · envelope `{ data: BondTypeConfigView[] }`).
 *
 * `label` + os `default*` vêm do catálogo (display read-only); `enabled` +
 * `version` vêm do overlay por-tenant (linha ausente = false / v0);
 * `activeBondsCount` é a contagem de instâncias de Bond do tenant por tipo.
 */
export interface BondTypeConfigView {
  bondType: BondType;
  label: string;
  defaultAdversarialMode: BondAdversarialMode;
  defaultSide: BondSide | null;
  defaultLifecycle: BondLifecycle;
  enabled: boolean;
  version: number;
  activeBondsCount: number;
}

/** Body do PATCH do toggle (PATCH /v1/bond-type-configs/:bondType · só `enabled`). */
export interface UpdateBondTypeConfigBody {
  enabled: boolean;
}

/**
 * Agrupamento canônico dos 21 tipos em 5 famílias — fato de produto (ADR-037/131,
 * gabarito Claude Design r5), NÃO dado de tenant: o GET não traz `family`, então
 * a constante vive no FE. A ordem dos tipos dentro de cada família segue o
 * catálogo. Os 21 tipos do catálogo estão cobertos exatamente uma vez.
 */
export interface BondFamily {
  id: string;
  label: string;
  types: readonly BondType[];
}

export const BOND_FAMILIES: readonly BondFamily[] = [
  {
    id: 'commercial',
    label: 'Comercial',
    types: ['contract', 'customer-account', 'opportunity', 'vendor-relationship'],
  },
  {
    id: 'legal',
    label: 'Jurídico',
    types: ['legal-case', 'mediation', 'compliance-case'],
  },
  {
    id: 'care',
    label: 'Cuidado',
    types: ['patient-care', 'home-care', 'social-care-case'],
  },
  {
    id: 'property',
    label: 'Propriedade',
    types: ['property', 'vehicle', 'equipment', 'project', 'partnership'],
  },
  {
    id: 'extra',
    label: 'Outros',
    types: [
      'student',
      'procurement',
      'interest',
      'purchase-intent',
      'transaction-attempt',
      'rental-contract',
    ],
  },
];
